"use client";

import { useCallback, useState } from "react";
import { X } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "@/modern/utils";
import { HTMLRenderer } from "@/modern/components/ui/html-renderer";
import type { InsightMessage, Persona } from "../context/AnalyzeFileContext";
import {
  ANALYZE_FEED_ROW_BODY_CLASS,
  ANALYZE_INSIGHT_RENDERER_CLASS,
  ANALYZE_SELECTION_CLASSES,
} from "../analyzeConstants";
import { AdvisorAccentBar } from "./AdvisorAccentBar";

const formatInsightTime = (timestamp: Date) =>
  new Date(timestamp).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type AnalyzeInsightCardProps = {
  message: InsightMessage;
  persona: Persona;
  onDelete: (id: string) => void;
  /** When sticky header already shows this advisor, skip the duplicate accent bar. */
  hideAccentBar?: boolean;
};

export const AnalyzeInsightCard = ({
  message,
  persona,
  onDelete,
  hideAccentBar = false,
}: AnalyzeInsightCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(() => {
    setIsDeleting(true);
    setTimeout(() => onDelete(message.id), 200);
  }, [message.id, onDelete]);

  return (
    <motion.article
      id={`analyze-insight-${message.id}`}
      data-insight-persona={persona.id}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: isDeleting ? 0 : 1, y: isDeleting ? 8 : 0 }}
      className={cn(
        "relative rounded-lg bg-muted/30 transition-shadow scroll-mt-2",
        hideAccentBar ? "overflow-hidden" : "flex gap-0 overflow-hidden",
      )}
    >
      {!hideAccentBar && <AdvisorAccentBar personaId={persona.id} />}
      <div className={cn(ANALYZE_FEED_ROW_BODY_CLASS, hideAccentBar && "px-2.5")}>
        <div className="mb-1.5 flex items-center justify-between gap-2 min-h-[1.25rem]">
          <time
            dateTime={message.timestamp.toISOString()}
            className="text-[11px] tabular-nums text-muted-foreground"
          >
            {formatInsightTime(message.timestamp)}
          </time>
          <button
            type="button"
            onClick={handleDelete}
            className="shrink-0 w-6 h-6 rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
            aria-label="Delete insight"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <HTMLRenderer
          content={message.content}
          className={cn(ANALYZE_INSIGHT_RENDERER_CLASS, ANALYZE_SELECTION_CLASSES)}
        />
      </div>
    </motion.article>
  );
};
